import {
    getCoprincipalAccountsByOidAndTid,
    getCoprincipalAccountsByPuidAndTid,
    getGlobalSettingsAccountKey,
} from 'owa-account-source-list-store';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { getQueryStringParameter } from 'owa-querystring';
import { getApplicationSettings } from 'owa-application-settings';
import {
    parseAuthenticatingIdentity,
    resolveCoprincipalMailboxInfoFromRouteKey,
} from './resolveMailboxFromRouteKey';

/**
 * Classification of how an applink route key relates to the current OWA session.
 *
 * - `current-account`: The route key targets the currently authenticated account. Proceed normally.
 * - `different-account`: The route key targets an account that should not use explicit logon.
 * - `shared-mailbox`: The authenticating identity matches the current account, but the
 *    destination is a shared/delegate mailbox under it. Redirect via explicit logon.
 * - `unknown`: Unable to classify (missing data). Proceed with existing fallback behavior.
 */
export type ApplinkAccountMatchType =
    | 'current-account'
    | 'different-account'
    | 'shared-mailbox'
    | 'unknown';

export interface ApplinkAccountClassification {
    matchType: ApplinkAccountMatchType;
    /** The decoded `ds` query parameter (destination SMTP), if present. */
    destinationSmtp?: string;
}

/**
 * Classifies how the applink route key relates to the current OWA session account.
 * This is a classification function with no state mutation — callers decide what action to take.
 *
 * @param routeKey The raw route key from the URL (e.g., `MBX%3A<guid>%40<tenant>`)
 * @returns Classification result indicating whether the current account matches, or what type of
 * redirect is needed.
 * @throws When invoked in Monarch, where applink route-key classification is never expected.
 */
export function classifyApplinkAccountMatch(
    routeKey: string,
    queryParams?: URLSearchParams
): ApplinkAccountClassification {
    if (getApplicationSettings('PlatformType').isMonarch) {
        const error: Error = new Error(
            'Unexpected applink account classification of route key in Monarch.'
        );
        throw error;
    }

    const mailboxInfoForRouteKey = resolveCoprincipalMailboxInfoFromRouteKey(routeKey);

    const currentAccountKey = getGlobalSettingsAccountKey();

    // If the route key (MBX / OID / PUID) resolves to the current session's account, proceed normally
    if (
        mailboxInfoForRouteKey &&
        getAccountKeyForMailboxInfo(mailboxInfoForRouteKey) === currentAccountKey
    ) {
        return { matchType: 'current-account' };
    }

    // Route key doesn't match the current account. Determine if it's a different user or a shared mailbox.
    const rawDs = getQueryParamValue(queryParams, 'ds');
    const destinationSmtp = decodeSafe(rawDs);

    // Check if the `ai` (authenticating identity) resolves to the current account.
    // If it does, the link targets a shared/archive mailbox under the current user.
    const rawAi = getQueryParamValue(queryParams, 'ai');
    const ai = decodeSafe(rawAi);

    if (ai) {
        const identity = parseAuthenticatingIdentity(ai);
        if (identity) {
            // Dispatch to the correct lookup based on the identity type so a PUID-prefixed
            // `ai` is resolved by PUID (not misinterpreted as an OID).
            const matchingAccounts =
                identity.identityType === 'puid'
                    ? getCoprincipalAccountsByPuidAndTid(identity.id, identity.tenantId)
                    : getCoprincipalAccountsByOidAndTid(identity.id, identity.tenantId);
            const aiMatchesCurrent = matchingAccounts.some(
                acct => getAccountKeyForMailboxInfo(acct.mailboxInfo) === currentAccountKey
            );

            if (aiMatchesCurrent) {
                // The authenticating identity IS the current account, but the route key
                // didn't resolve to it → this targets a shared/archive mailbox
                return { matchType: 'shared-mailbox', destinationSmtp };
            }
        }
    }

    // If the route key resolves to a different coprincipal account in the store (multi-account
    // scenario, unlikely in OWA but defensive), or doesn't resolve, treat as different account
    if (mailboxInfoForRouteKey) {
        // Different coprincipal account exists locally but isn't the global settings account
        return { matchType: 'different-account', destinationSmtp };
    }

    // Route key not found locally and ai doesn't match → different account entirely
    return { matchType: 'different-account', destinationSmtp };
}

function getQueryParamValue(
    queryParams: URLSearchParams | undefined,
    key: string
): string | undefined {
    if (queryParams) {
        return queryParams.get(key) ?? undefined;
    }

    return getQueryStringParameter(key);
}

function decodeSafe(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}
